import { useCallback, useMemo, useRef } from 'react';
import useAxios from './useAxios';
import { debounce } from '../utils/helper';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';
import { useParams } from 'react-router-dom';


const useCaseFieldUpdate = ({ debounceTime = 800 } = {}) => {
  const { postData, isLoading, error } = useAxios();
  const { caseId } = useParams();

  const updateCaseById = useCallback(async (value) => {
    if (!caseId) {
      console.warn('Case ID is required for field update');
      return;
    }
    const response = await postData(`/updateCase/${caseId}`, {
      action: UPDATE_CASE_ACTION.UPDATE_BY_ID,
      data: value
    });

    return response;
  }, [caseId, postData]);

  const updateCase = useCallback(async ({ data, action = UPDATE_CASE_ACTION.UPDATE }) => {
    if (!caseId) {
      console.warn('Case ID is required for field update');
      return;
    }

    // Debug: Log all case updates
    console.log('🔵 [useCaseFieldUpdate] updateCase called:', {
      caseId,
      data,
      action,
      timestamp: new Date().toISOString()
    });

    // Debug: Check if howincidentOccured field
    const dataKeys = Object.keys(data || {});
    const hasHowIncident = dataKeys.some(key => key.includes('howincidentOccured'));

    if (hasHowIncident) {
      console.log('🟢 [useCaseFieldUpdate] SAVING howincidentOccured:', {
        data,
        dataKeys
      });
    }

    const response = await postData(`/updateCase/${caseId}`, {
      action,
      data
    });

    // Debug: Log response
    if (hasHowIncident) {
      console.log('🟢 [useCaseFieldUpdate] howincidentOccured save response:', response);
    }

    return response;
  }, [caseId, postData]);

  // merge per-field payloads so editing several fields within the debounce window all persist
  const pendingUpdateRef = useRef({ data: {}, action: UPDATE_CASE_ACTION.UPDATE });

  const flushPendingUpdate = useMemo(
    () => debounce(() => {
      const { data, action } = pendingUpdateRef.current;
      pendingUpdateRef.current = { data: {}, action: UPDATE_CASE_ACTION.UPDATE };
      if (Object.keys(data).length === 0) return;
      updateCase({ data, action });
    }, debounceTime),
    [updateCase, debounceTime]
  );

  const debounceUpdateCase = useCallback(({ data, action = UPDATE_CASE_ACTION.UPDATE }) => {
    pendingUpdateRef.current = {
      data: { ...pendingUpdateRef.current.data, ...data },
      action
    };
    flushPendingUpdate();
  }, [flushPendingUpdate]);

  // sends the queued payload right away so a follow up write cannot race the debounced one
  const flushCaseUpdate = useCallback(() => flushPendingUpdate.flush(), [flushPendingUpdate]);

  return {
    updateCase,
    debounceUpdateCase,
    flushCaseUpdate,
    updateCaseById,
    isLoading,
    error
  };
};

export default useCaseFieldUpdate;
